import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { PROJECT_CREATION_PAUSED, V2_UPDATE_DATE_TEXT } from '../../config/maintenance';
import ProjectDetailModal from './project/modals/ProjectDetailModal';
import { readWarm } from '../../utils/warmApplyCache';
import { clearPendingCache, warmPending } from '../../utils/warmPendingCache';
import '../../styles/common/ib-layout.css';
import '../../styles/common/ib-forms.css';

const PAGE_SIZE = 15;
const ss = typeof window !== 'undefined' ? window.sessionStorage : null;

const fmt = (n) => {
  const v = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(v) ? v.toLocaleString('ko-KR') : (n ?? '');
};

// 마감일까지 남은 일수 계산
const getDeadlineText = (recruitEnd) => {
  if (!recruitEnd) return null;
  
  const now = new Date();
  const endDate = new Date(recruitEnd);
  
  // 시간 무시하고 날짜만 비교 (KST 기준)
  now.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return null; // 마감됨
  if (diffDays === 0) return '오늘 모집 마감!';
  if (diffDays === 1) return '모집 마감 1일 전';
  if (diffDays === 2) return '모집 마감 2일 전';
  if (diffDays === 3) return '모집 마감 3일 전';
  
  return `모집 마감 ${diffDays}일 전`; // 모든 경우에 표시
};

// 서비스 타입 한글 변환
const getServiceTypeLabel = (type) => {
  const labels = {
    delivery: '제품 제공',
    purchase: '제품 미제공',
    visit: '장소 광고',
    custom: '광고주 자율 방식',
    free: '광고주 자율 방식'
  };
  return labels[type] || type;
};

function cacheSet(k, v){ try{ ss?.setItem(k, JSON.stringify(v)); }catch{} }

// LRU 캐시 헬퍼
function setCacheWithLimit(map, key, value, maxSize = 50) {
  if (map.has(key)) {
    map.delete(key);
  }
  if (map.size >= maxSize) {
    const firstKey = map.keys().next().value;
    map.delete(firstKey);
  }
  map.set(key, value);
}

export default function ProjectApplyList() {
  const [uid, setUid] = useState(window.__ME__?.id);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [sort, setSort] = useState('deadline');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const fetchKeyRef = useRef('');

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const detailViewCache = useRef(new Map());
  const cardRefs = useRef(new Map());
  const observerRef = useRef(null);

  useEffect(() => {
    const handleUserReady = (event) => {
      const newUid = event?.detail?.id || window.__ME__?.id;
      if (newUid) {
        setUid(newUid);
      }
    };

    window.addEventListener('user-ready', handleUserReady);
    return () => window.removeEventListener('user-ready', handleUserReady);
  }, []);

  useEffect(() => {
    const handleUpdate = () => {
      if (!uid) return; 
      
      const lastLoginUid = sessionStorage.getItem('last_login_uid');
      if (uid !== lastLoginUid) return; 
      
      const warm = readWarm(uid, sort, page, PAGE_SIZE);
      if (warm?.items) {
        setItems(warm.items);
        if (typeof warm.total === 'number') setTotal(Number(warm.total || 0));
        
        warm.items.forEach(p => {
          if (p?.id) {
            setCacheWithLimit(detailViewCache.current, p.id, { ...p }, 50);
          }
        });
      }
    };
  
    window.addEventListener('project-list-update', handleUpdate);
    return () => window.removeEventListener('project-list-update', handleUpdate);
  }, [sort, page, uid]);

  const handleApplySuccess = useCallback(async (projectId) => {
    const currentUid = uid;
    
    setItems(prevItems =>
      prevItems.map(item =>
        item?.id === projectId
          ? {
              ...item,
              isApplied: true,
              applicationStatus: 'pending',
              appliedCount: (item.appliedCount || 0) + 1
            }
          : item
      )
    );

    if (currentUid) {
      const currentKey = `INF_LIST:${currentUid}:${sort}:${page}:${PAGE_SIZE}`;
      try {
        const cached = ss?.getItem(currentKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed.items)) {
            parsed.items = parsed.items.map(item =>
              item?.id === projectId
                ? {
                    ...item,
                    isApplied: true,
                    applicationStatus: 'pending',
                    appliedCount: (item.appliedCount || 0) + 1
                  }
                : item
            );
            cacheSet(currentKey, parsed);
          }
        }
      } catch (err) {
        console.error('sessionStorage 업데이트 실패:', err);
      }
    }

    if (projectId && detailViewCache.current.has(projectId)) {
      const cached = detailViewCache.current.get(projectId);
      setCacheWithLimit(detailViewCache.current, projectId, {
        ...cached,
        isApplied: true,
        applicationStatus: 'pending',
        appliedCount: (cached.appliedCount || 0) + 1
      }, 50);
    }

    if (currentUid) {
      setTimeout(async () => {
        try {
          clearPendingCache(currentUid);
          await warmPending({ uid: currentUid, awaitReady: 'all' });
          window.dispatchEvent(new CustomEvent('pending-list-update'));
        } catch (err) {
          console.error('Pending 캐시 업데이트 실패:', err);
        }
      }, 0);
    }
  }, [sort, page, uid]);

  const prepareCardDetail = useCallback(async (p) => {
    if (!p || detailViewCache.current.has(p.id)) return;
    const urls = [p.cover].concat(Array.isArray(p.gallery) ? p.gallery : []).filter(Boolean);
    
    await Promise.all(urls.map(u => new Promise(res => {
      try {
        const img = new Image();
        img.onload = img.onerror = () => res();
        img.src = u;
        if (img.decode) img.decode().then(res).catch(res);
      } catch { res(); }
    })));
    
    setCacheWithLimit(detailViewCache.current, p.id, { ...p }, 50);
  }, []);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    
    const currentCardRefs = cardRefs.current;
    
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const projectId = entry.target.dataset.projectId;
          const project = itemsRef.current.find(p => p?.id === projectId);
          if (project && !detailViewCache.current.has(projectId)) {
            prepareCardDetail(project);
          }
        }
      });
    }, { rootMargin: '50px' });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      currentCardRefs.clear();
    };
  }, [prepareCardDetail]);

  const registerCard = (el, projectId) => {
    if (!el || !projectId) return;
    const prevEl = cardRefs.current.get(projectId);
    if (prevEl && prevEl !== el) {
      observerRef.current?.unobserve(prevEl);
    }
    cardRefs.current.set(projectId, el);
    observerRef.current?.observe(el);
  };

  const totalPages = useMemo(() => {
    if (total === 0) return 1;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [total]);

  useEffect(() => {
    if (!uid) {
      setInitialized(false);
      return;
    }
    
    setInitialized(true);
    setLoading(true);
    
    const key = `INF_LIST:${uid}:${sort}:${page}:${PAGE_SIZE}`;
    const fetchKey = `${sort}:${page}`;
  
    const lastLoginUid = sessionStorage.getItem('last_login_uid');
    
    if (uid === lastLoginUid) {
      const warm = readWarm(uid, sort, page, PAGE_SIZE);
      
      if (warm?.items) {
        setItems(warm.items);
        if (typeof warm.total === 'number') setTotal(Number(warm.total||0));
        warm.items.forEach(p => { 
          if (p?.id) setCacheWithLimit(detailViewCache.current, p.id, { ...p }, 50); 
        });
      }
    }
    
    sessionStorage.setItem('last_login_uid', uid);

    const ctl = new AbortController();
    (async () => {
      if (fetchKeyRef.current === fetchKey) {
        setLoading(false);
        return;
      }
      fetchKeyRef.current = fetchKey;
      
      try {
        const qs = new URLSearchParams({ 
          sort, 
          page: String(page), 
          limit: String(PAGE_SIZE)
        }).toString();
        
        const res = await fetch(`/api/influencer/mybiz/projects/available?${qs}`, { credentials:'include', signal: ctl.signal });
        const j = await res.json().catch(() => ({}));
        
        if (res.ok && j?.ok) {
          const list = Array.isArray(j.items) ? j.items : [];
          
          const byId = new Map((itemsRef.current || []).map(x => [x?.id, x]));
          const merged = list.map(n => {
            const o = byId.get(n?.id);
            if (!o) return n;
            return { 
              ...n, 
              cover: o.cover || n.cover, 
              gallery: (Array.isArray(o.gallery) && o.gallery.length) ? o.gallery : n.gallery,
              applicationStatus: n.applicationStatus !== undefined ? n.applicationStatus : (o.applicationStatus ?? null),
              isApplied: typeof n.isApplied === 'boolean' ? n.isApplied : !!o.isApplied
            };
          });
          
          setItems(merged);
          if (typeof j.total === 'number') setTotal(Number(j.total || 0));
          
          if (merged.length > 0 || (page === 1 && typeof j.total === 'number')) {
            cacheSet(key, { items: merged, total: j.total });
          }
          
          if (page === 1 && merged.length > 0) {
            cacheSet(`INF_LIST_LAST:${uid}`, { items: merged, total: j.total });
          }
          
          merged.forEach(p => { 
            if (p?.id) setCacheWithLimit(detailViewCache.current, p.id, { ...p }, 50); 
          });
        } else {
          fetchKeyRef.current = '';
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          fetchKeyRef.current = '';
        }
      } finally {
        setLoading(false);
      }
    })();
    
    return () => ctl.abort();
  }, [sort, page, uid]);

  // 지원 상태 → 배지 (기존 8분기 상태 문구 유지)
  const statusBadge = (p) => {
    if (!p?.applicationStatus) return { cls: 'badge--mint', label: '모집 중' };
    const s = p.applicationStatus;
    if (s === 'pending') return { cls: 'badge--blue', label: '지원 완료' };
    if (s === 'selected' && p.contractStatus === 'accepted') return { cls: 'badge--violet', label: '계약 완료' };
    if (s === 'selected' && p.contractStatus === 'rejected') return { cls: 'badge--red', label: '계약 거절' };
    if (s === 'selected') return { cls: 'badge--blue', label: '선정됨' };
    if (s === 'accepted') return { cls: 'badge--violet', label: '수락됨' };
    if (s === 'rejected') return { cls: 'badge--red', label: '거절됨' };
    if (s === 'expired') return { cls: 'badge--gray', label: '계약 만료' };
    return { cls: 'badge--gray', label: s };
  };

  const openDetail = (p) => {
    if (!p) return;
    const cachedView = detailViewCache.current.get(p.id);
    setDetail(cachedView || p);
    setOpen(true);
  };

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">프로젝트 지원하기</h2>
        <p className="mb-page-description">
          {total > 0 ? `모집 중 ${fmt(total)}개 · ` : ''}내 SNS와 맞는 프로젝트를 찾아 지원해보세요.
        </p>
      </div>

      {/* 정렬 칩 */}
      <div className="chips">
        {[['deadline', '마감 임박순'], ['created', '최신순']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`chip${sort === key ? ' is-active' : ''}`}
            onClick={() => { setPage(1); setSort(key); }}
          >
            {label}
          </button>
        ))}
      </div>

      {!initialized ? (
        <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
          프로젝트를 불러오는 중...
        </div>
      ) : items.length === 0 && total === 0 && fetchKeyRef.current !== '' ? (
        <div className="mb-card" style={{ padding: '72px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>
            모집 중인 프로젝트가 없어요
          </div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>
            {/* v2 전환 기간에는 기존 「1~2일 후」 문구를 대체 (10/10 배포 시 원복) */}
            {PROJECT_CREATION_PAUSED
              ? `${V2_UPDATE_DATE_TEXT} 업데이트 준비 기간이에요. 새 프로젝트는 업데이트 이후 순차적으로 열립니다.`
              : '1~2일 후 다시 확인해보세요!'}
          </div>
        </div>
      ) : (
        (items.length ? items : Array.from({ length: 4 })).map((p, idx) => {
          if (!p) {
            return (
              <div className="offer" key={`sk-${idx}`} aria-hidden="true">
                <div style={{ height: 46, background: '#f3f4f6', borderRadius: 13, marginBottom: 12 }} />
                <div style={{ height: 64, background: '#f3f4f6', borderRadius: 12 }} />
              </div>
            );
          }
          const badge = statusBadge(p);
          const applied = !!p.isApplied;
          const deadlineText = getDeadlineText(p.recruitEnd);
          const subParts = [
            getServiceTypeLabel(p.serviceType),
            (Array.isArray(p.platforms) && p.platforms.length > 0) ? p.platforms.join(' · ') : null,
            `모집 ${fmt(p.headcount || 0)}명`,
            deadlineText,
          ].filter(Boolean);
          return (
            <div
              key={p.id}
              ref={(el) => registerCard(el, p.id)}
              data-project-id={p.id}
              className={`offer${applied ? ' is-applied' : ''}`}
              onMouseEnter={() => prepareCardDetail(p)}
            >
              <div className="offer__head">
                <span className="offer__logo" aria-hidden="true">
                  {p.cover
                    ? <img src={p.cover} alt="" loading="eager" decoding="async" />
                    : (p.productName || '프').charAt(0)}
                </span>
                <span className="offer__meta">
                  <span className="offer__t" style={{ display: 'block' }}>{p.productName || '광고 프로젝트'}</span>
                  <span className="offer__s" style={{ display: 'block' }}>{subParts.join(' · ')}</span>
                </span>
                <span className={`badge ${badge.cls}`}>{badge.label}</span>
              </div>

              <div className="offer__reward">
                <div><b className="is-mint">{fmt(p.rewardPointPerCreator)}P</b><span>기본 보상</span></div>
                <div>
                  {p?.perPurchaseReward?.use
                    ? <><b className="is-violet">+{fmt(p.perPurchaseReward.rewardPerBuyer)}P</b><span>구매 1명당 (CPS)</span></>
                    : <><b>—</b><span>CPS 없음</span></>}
                </div>
                <div><b>{fmt(p.appliedCount || 0)}명</b><span>현재 지원</span></div>
              </div>

              <div className="offer__foot">
                {applied ? (
                  <>
                    <span className="t-sub">이미 지원했어요 · 결과는 "지원 중인 프로젝트"에서 확인</span>
                    <button type="button" className="mb-btn mb-btn-secondary mb-btn-sm" onClick={() => openDetail(p)}>자세히 보기</button>
                  </>
                ) : (
                  <>
                    <span className="t-sub">{deadlineText || '자세히 보기에서 조건 확인 후 지원할 수 있어요'}</span>
                    <button type="button" className="mb-btn mb-btn-secondary mb-btn-sm" onClick={() => openDetail(p)}>자세히 보기</button>
                    <button type="button" className="mb-btn mb-btn-primary" onClick={() => openDetail(p)}>이 프로젝트 지원하기</button>
                  </>
                )}
              </div>
            </div>
          );
        })
      )}

      {loading && items.length > 0 && (
        <div className="mbh-loading" style={{ textAlign: 'center' }}>새 목록을 불러오는 중…</div>
      )}

      {initialized && totalPages > 1 && !(items.length === 0 && total === 0 && fetchKeyRef.current !== '') && (
        <div className="pager">
          <button type="button" onClick={() => setPage(1)} disabled={page <= 1} title="첫 페이지">«</button>
          <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} title="이전 페이지">‹</button>
          {page} / {totalPages}
          <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} title="다음 페이지">›</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages} title="마지막 페이지">»</button>
        </div>
      )}

      <ProjectDetailModal
        open={open}
        onClose={()=>{ setOpen(false); }}
        project={detail}
        keepMounted
        onApplySuccess={handleApplySuccess}
      />
    </div>
  );
}
